import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCPopover, RCCServicesApi, RCCTextArea, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import CheckPermission from "src/components/CheckPermission";
import List from "src/components/List";
import ModalDescription from "src/components/ModalDescription";
import { IRefDescriptionModal } from "src/components/ModalDescription/interface";
import Select from "src/components/Select";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import formatNumber from "src/utils/formatNumber";
import { IMdtba002, IMdtba003, IMdtba004, IMdtbb002, IRefDiagnosticQuestions} from "../../../interface";
import WrapContext from "../../../styles";

interface IQuestionsData{
	selectedDiagnostic: number;
	questionsData: IMdtba003[];
	criteria: IMdtba002;
	setQuestionsData: (value: IMdtba003[] | ((prevVar: IMdtba003[]) => IMdtba003[])) => void;
}

const DiagnosticQuestions: React.ForwardRefRenderFunction<IRefDiagnosticQuestions, IQuestionsData> = ({
	selectedDiagnostic,
	questionsData,
	criteria,
	setQuestionsData
}, ref) => {

	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { checkIfHasSpecificPermission } = useAuth();

	const modalDescriptionRef = useRef<IRefDescriptionModal>(null);

	const [buttonLoadingState, setButtonLoadingState] = useState(false);
	const [listData, setListData] = useState<IMdtba003[]>([]);
	
	const { 
		handleSubmit, 
		register, 
		reset, 
		control, 
		clearErrors, 
		setError 
	} = useForm(); 

	const permisionToCreateUpdate = checkIfHasSpecificPermission("/diagnostics", PermissionEnum.CREATE_UPDATE);
	
	const getPercentage = (row: any, questionIndex: number | undefined) => {
		if(!questionIndex && questionIndex !== 0) 
			return `${formatNumber(0, currentLanguage)}%`;
		const answer = questionsData[questionIndex].mdtbb002?.mdb002Mda004Id;
		const question = row.mdtba004.find((item: any) => item.mda004Id === answer);
		return `${formatNumber(question?.mda004Percentual, currentLanguage)}%`;
	};

	const getDescription = (row: any, questionIndex: number | undefined) => {
		if(!questionIndex && questionIndex !== 0) 
			return `${formatNumber(0, currentLanguage)}%`;
		const answer = questionsData[questionIndex].mdtbb002?.mdb002Mda004Id;
		const question = row.mdtba004.find((item: any) => item.mda004Id === answer);
		return question?.mda004Descricao;
	};

	const colTitle = (row: any) => (
		<RCCGrid
			container
			item
			wrap="nowrap"
		>
			<RCCButton
				color="transparent"
				icon={{
					name: "infoCircle",
					color: "var(--grey)"
				}}
				layout="isRounded"
				onClick={async (e) => {
					e.stopPropagation();
					showDescription(row.mda003Questao, row.mda003Descricao);
				}}
				tabIndex={-1}
				styleInline="span{ color: var(--grey); }"
			/>
			<WrapContext>
				<b>{`(${row.mda003Pontuacao}pts)`}</b> &nbsp;{`${row.mda003Questao} `} 
			</WrapContext>
		</RCCGrid>
	);

	const colAnswer = (row: any, index:  number | undefined) => (
		<>
			<input 
				type="text"
				value={row.mda003Id}
				{...register(`mdtba003.[${index}].mda003Id`)} 
				style={{ display: "none" }} 
				readOnly
			/>
			<Select 
				control={control}
				name={`mdtba003.[${index}].mda004Resposta`}
				styleInline="margin-right: 4px;height: unset; min-height: unset; padding-top: 0px;"
				label=""
				options={row.mdtba004.map((item: any) => ({
					value: item.id,
					label: item.mda004Resposta,
					questionIndex: index
				}))}
				disabled={!permisionToCreateUpdate}
				onChangeCapture={(e: any) => {
					if(index || index === 0) 
						setQuestionsData((prevState) => {
							const updateState = [...prevState];
							if(!updateState[index].mdtbb002)
								updateState[index].mdtbb002 = {} as IMdtbb002;
							updateState[index].mdtbb002.mdb002Mda004Id = e?.value ? parseInt(e.value,10): 0;
							return updateState;
						});
				}}
				defaultValue={row.mdtbb002?.mdb002Mda004Id ? row.mdtbb002?.mdb002Mda004Id.toString() : undefined}
				isClearable
			/>		
		</>
	);

	const colInfo = (row: any, questionIndex: number | undefined) => {

		const percentage = getPercentage(row, questionIndex);
		const description = getDescription(row, questionIndex);

		return (<RCCGrid
			container
			item
			justifyContent="center"
		>
			<span style={{ marginRight: "4px" }}>
				{percentage}
			</span>
			{description && (
				<RCCPopover
					header={{title:`${translate("Texto informativo")}`}}
					body={{text: description}}
					position="leftEnd"
					styleInline="margin-left: 4px;"
				>
					<RCCIcon
						name="infoCircle"
						size={16}
					/>
				</RCCPopover>
			)}
		</RCCGrid>
		);
	};
		
	const colShowComplement = (row: any, questionIndex: number | undefined) => (
		<RCCTooltip
			text={translate("Comentário")}
			color={row?.mdtbb002?.mdb002Mda004Id && row?.mdtbb002?.mdb002Complemento  ? "blue" : "grey"}
			position="bottom"
		>
			<RCCButton
				layout="isRounded"
				icon={{
					name: "comment"
				}}
				color={row?.mdtbb002?.mdb002Mda004Id && row?.mdtbb002?.mdb002Complemento ? "blue" : "grey"}
				disabled={!row?.mdtbb002?.mdb002Mda004Id}
				onClick={() => {
					setListData((prevState) => {
						const updateState = [...prevState];
						updateState[questionIndex ?? 0].toogleButton = 	!prevState[questionIndex ?? 0].toogleButton;
						return updateState;
					});
				}}
			/>
		</RCCTooltip>
	);

	const submitAnswers = async (dataX: any) => {
		try{
			setButtonLoadingState(true);
			clearErrors();
			const dataForm = dataX;

			dataForm.mdtbb002 = dataForm.mdtba003?.map((mdtba003: any) => ({
				mdb002Mdb001Id: selectedDiagnostic,
				mdb002Complemento: mdtba003.mda003Descricao ?? "",
				mdb002Mda003Id: mdtba003.mda003Id ? parseInt(mdtba003.mda003Id, 10) : undefined,
				mdb002Mda004Id: mdtba003.mda004Resposta? parseInt(mdtba003.mda004Resposta, 10) : -1
			}));

			await RCCServicesApi.postRequest({
				method: "Diagnostics/AnswerQuestions",
				body: {
					mdtbb002: dataX.mdtbb002,
					mdb001Id: selectedDiagnostic
				}
			});
	
			addToast({
				color: "green",
				description: translate("Respostas salvas com sucesso"),
				title: translate("Sucesso")
			});
			setButtonLoadingState(false);
		}catch(err: any){
			setButtonLoadingState(false);
			if(getValidationErrors(err, setError))
				return;
			standardErrorMessages(err, translate("Erro ao salvar respostas"), addToast);
		}
	};

	const showDescription = (title: string, description: string) => {
		modalDescriptionRef.current?.openModal(title, description);
	};

	useImperativeHandle(ref, () => ({
		reset
	}));

	const getPerformedScore = () => {
		let performedScore = 0;

		questionsData.forEach((question: IMdtba003) => {
			const answer = question.mdtbb002?.mdb002Mda004Id;
			if(!answer) return;
			const questionpercentage = question.mdtba004.find((item: IMdtba004) => item.mda004Id === answer)?.mda004Percentual ?? 0;
			performedScore += (question.mda003Pontuacao * questionpercentage) / 100 ;
		});

		return formatNumber(performedScore, currentLanguage, 2) ;
	};

	const listChild = (dt: any) => (
		<RCCGrid
			container
			item
			justifyContent="flex-end"
		>
			<RCCGrid container item xs={12} sm={5} />
			<RCCGrid
				container
				item	
				xs={12}
				sm={5}		
			>
				<RCCTextArea 
					styleInline="margin-right: 4px; margin-bottom: 10px;"
					name={`mdtba003.[${dt.index}].mda003Descricao`}
					label={translate("Complemento")}
					maxLength={4000}
					register={register}
					defaultValue={dt.mdtbb002?.mdb002Complemento ?? ""}
					onBlurCapture={(newValue:any) => {
						if((dt.mdtbb002?.mdb002Complemento === "" || dt.mdtbb002?.mdb002Complemento?.trim() === "") && newValue.target.value !== ""){
							setListData((prevState) => {
								const updateState = [...prevState];
								updateState[dt.index].mdtbb002.mdb002Complemento = 	newValue.target.value;
								return updateState;
							});
						}else if((dt.mdtbb002?.mdb002Complemento !== "" && dt.mdtbb002?.mdb002Complemento?.trim() !== "") && (newValue.target.value === "" || newValue.target.value?.trim() === "")){
							setListData((prevState) => {
								const updateState = [...prevState];
								updateState[dt.index].mdtbb002.mdb002Complemento = 	newValue.target.value;
								return updateState;
							});
						}
					}}
					disabled={!questionsData[dt.index]?.mdtbb002?.mdb002Mda004Id || !permisionToCreateUpdate}
				/>
			</RCCGrid>	
			<RCCGrid container item xs={12} sm={1} />
			<RCCGrid container item xs={12} sm={1} />
		</RCCGrid>		
	);

	useEffect(() => {
		setListData([
			...questionsData.map((mdtba003: IMdtba003, index) => {
				const mdtba003Changed: any = mdtba003;
				mdtba003Changed.toogleButton = false;
				mdtba003Changed.index = index;
				return mdtba003Changed;
			})
		]);
	}, [questionsData]);

	return (
		<>
			<RCCGrid
				container
				item
			>
			 
				<List 
					data={listData}
					showHeader
					styleInline=".is-children {background-color: unset !important;}"
					childrenOfLine={{
						cellRenderer: listChild,
						conditionToRender: (row: any) => row.toogleButton
					}}
					columns={[{
						idColumn: "mda003Questao",
						nameColumn: translate("Questão"),
						justify: "left",
						xs: 12,
						sm: 5,
						cellRenderer: colTitle,
						truncate: true
					},{
						idColumn: "mda003Questao",
						nameColumn: translate("Resposta"),
						justify: "left",
						xs: 12,
						sm: 5,
						cellRenderer: colAnswer
					},{
						idColumn: "mda003Pontuacao",
						nameColumn: translate("Realização"),
						xs: 12,
						sm: 1,
						cellRenderer: colInfo
					},{
						idColumn: "",
						nameColumn: "",
						xs: 12,
						sm: 1,
						cellRenderer: colShowComplement
					}]}
					layout="table"
				/>

				<RCCGrid
					item
					container
					justifyContent="space-between"
					alignItems="center"
				>
					<span style={{ fontSize: "0.8rem" }}>
						<b>{translate("Pontuação")}:</b> {getPerformedScore()} {translate("realizados de")} {formatNumber(criteria.mda002Pontuacaomax, currentLanguage, 2)} {translate("máximos")}
					</span>	
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCButton
							icon={{name: "checkCircle"}}
							color="green"
							style={{ marginTop: "1rem" }}
							size="sm"
							layout="default"
							loading={buttonLoadingState}
							onClick={handleSubmit(submitAnswers)}
						>
							{translate("Confirmar alteração")}
						</RCCButton>												
					</CheckPermission>
				</RCCGrid>
			
			</RCCGrid>

			<ModalDescription 
				ref={modalDescriptionRef} 	
			/>
			
		</>
	);
};


export default forwardRef(DiagnosticQuestions);
